/**
 * FeedbackWidget — floating feedback button + popup form.
 * Appears on every page, bottom-right corner.
 * Submits to /api/feedback (to be wired to backend later).
 */
import { FC, useState, useRef, useEffect } from 'react';
import { useTheme } from '../../context/theme-context';

type Category = 'idea' | 'bug' | 'question' | 'other';

const CATEGORIES: { value: Category; label: string; emoji: string }[] = [
    { value: 'idea',     label: 'Idea',     emoji: '💡' },
    { value: 'bug',      label: 'Bug',      emoji: '🐛' },
    { value: 'question', label: 'Question', emoji: '❓' },
    { value: 'other',    label: 'Other',    emoji: '💬' },
];

const FONT_BODY    = 'Inter, sans-serif';
const FONT_HEADING = 'Montserrat, sans-serif';

const FeedbackWidget: FC = () => {
    const [open, setOpen]         = useState(false);
    const [category, setCategory] = useState<Category>('idea');
    const [message, setMessage]   = useState('');
    const [status, setStatus]     = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
    const panelRef = useRef<HTMLDivElement>(null);
    const { isDark } = useTheme();

    useEffect(() => {
        if (!open) return;
        const handler = (e: MouseEvent) => {
            if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    useEffect(() => {
        if (!open) {
            setTimeout(() => {
                setMessage('');
                setStatus('idle');
                setCategory('idea');
            }, 300);
        }
    }, [open]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!message.trim()) return;
        setStatus('sending');
        try {
            console.log('[Feedback]', { category, message });
            await new Promise(r => setTimeout(r, 800));
            setStatus('sent');
        } catch {
            setStatus('error');
        }
    };

    const panelBg     = isDark ? 'rgba(15,14,12,0.97)'  : 'rgba(255,255,255,0.99)';
    const borderColor = isDark ? 'rgba(212,178,84,0.25)' : 'rgba(212,178,84,0.35)';
    const textPrimary = isDark ? '#ffffff'               : '#1F1F1F';
    const textMuted   = isDark ? '#9CA3AF'               : '#6B6B6B';
    const inputBg     = isDark ? 'rgba(15,14,12,0.60)'   : '#FFFFFF';
    const inputBorder = isDark ? 'rgba(212,178,84,0.20)' : 'rgba(212,178,84,0.30)';
    const catActiveBg = 'rgba(212,178,84,0.18)';
    const catBg       = isDark ? 'rgba(15,14,12,0.50)'   : '#F5F3EE';

    return (
        <>
            {/* Floating trigger */}
            <button
                type="button"
                onClick={() => setOpen(o => !o)}
                title="Send feedback"
                aria-label="Send feedback"
                style={{
                    position: 'fixed',
                    bottom: 72,
                    right: 24,
                    zIndex: 9000,
                    width: 52,
                    height: 52,
                    borderRadius: '50%',
                    border: 'none',
                    cursor: 'pointer',
                    background: 'linear-gradient(135deg, #D4B254 0%, #B89741 100%)',
                    color: '#1A1A1A',
                    boxShadow: '0 4px 20px rgba(212,178,84,0.45), 0 2px 8px rgba(0,0,0,0.25)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s ease',
                    transform: open ? 'scale(0.92) rotate(45deg)' : 'scale(1)',
                }}
                onMouseEnter={e => {
                    if (!open) (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1.08)';
                }}
                onMouseLeave={e => {
                    if (!open) (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)';
                }}
            >
                {open ? (
                    <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                ) : (
                    <svg width="22" height="22" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                            d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                )}
            </button>

            {/* Popup panel */}
            <div
                ref={panelRef}
                style={{
                    position: 'fixed',
                    bottom: 136,
                    right: 24,
                    zIndex: 8999,
                    width: 320,
                    borderRadius: '16px',
                    background: panelBg,
                    border: `1px solid ${borderColor}`,
                    backdropFilter: 'blur(20px)',
                    boxShadow: isDark
                        ? '0 20px 40px rgba(0,0,0,0.6), 0 0 0 1px rgba(212,178,84,0.12)'
                        : '0 20px 40px rgba(0,0,0,0.12), 0 0 0 1px rgba(212,178,84,0.15)',
                    overflow: 'hidden',
                    opacity: open ? 1 : 0,
                    transform: open ? 'translateY(0) scale(1)' : 'translateY(12px) scale(0.97)',
                    pointerEvents: open ? 'auto' : 'none',
                    transition: 'opacity 0.22s ease, transform 0.22s ease',
                    transformOrigin: 'bottom right',
                }}
            >
                {/* Header */}
                <div style={{
                    padding: '16px 20px 12px',
                    borderBottom: `1px solid ${borderColor}`,
                    background: isDark ? 'rgba(212,178,84,0.06)' : 'rgba(212,178,84,0.04)',
                }}>
                    <p style={{ margin: 0, fontFamily: FONT_HEADING, fontWeight: 800, fontSize: '0.95rem', textTransform: 'uppercase', letterSpacing: '-0.01em', color: '#D4B254' }}>
                        Share Feedback
                    </p>
                    <p style={{ margin: '4px 0 0', fontFamily: FONT_BODY, fontSize: '0.75rem', color: textMuted, fontWeight: 400 }}>
                        Ideas, bugs, questions — we read everything.
                    </p>
                </div>

                {status === 'sent' ? (
                    <div style={{ padding: '32px 20px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                        <div style={{ width: 52, height: 52, borderRadius: '50%', background: 'rgba(212,178,84,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, color: '#D4B254' }}>✓</div>
                        <p style={{ margin: 0, fontFamily: FONT_HEADING, fontWeight: 700, fontSize: '0.9rem', textTransform: 'uppercase', color: '#D4B254' }}>Thank you!</p>
                        <p style={{ margin: 0, fontFamily: FONT_BODY, fontSize: '0.8rem', color: textMuted, lineHeight: 1.5 }}>
                            Your feedback has been received. We appreciate you helping us improve!
                        </p>
                        <button
                            type="button"
                            onClick={() => setOpen(false)}
                            style={{ marginTop: 4, padding: '8px 20px', borderRadius: 9999, border: `1px solid ${borderColor}`, background: 'transparent', color: textMuted, fontFamily: FONT_BODY, fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', cursor: 'pointer', transition: 'all 0.15s ease' }}
                            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.color = '#D4B254'; (e.currentTarget as HTMLButtonElement).style.borderColor = '#D4B254' }}
                            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.color = textMuted; (e.currentTarget as HTMLButtonElement).style.borderColor = borderColor }}
                        >Close</button>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} style={{ padding: '16px 20px 20px' }}>
                        {/* Category */}
                        <div style={{ marginBottom: 14 }}>
                            <p style={{ margin: '0 0 8px', fontFamily: FONT_BODY, fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: textMuted }}>Category</p>
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                {CATEGORIES.map(cat => (
                                    <button
                                        key={cat.value}
                                        type="button"
                                        onClick={() => setCategory(cat.value)}
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: 5,
                                            padding: '5px 10px', borderRadius: 9999,
                                            border: `1px solid ${category === cat.value ? '#D4B254' : borderColor}`,
                                            background: category === cat.value ? catActiveBg : catBg,
                                            color: category === cat.value ? '#D4B254' : textMuted,
                                            fontFamily: FONT_BODY, fontWeight: 600, fontSize: '0.72rem',
                                            cursor: 'pointer', transition: 'all 0.15s ease',
                                        }}
                                    >
                                        <span>{cat.emoji}</span><span>{cat.label}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Message */}
                        <div style={{ marginBottom: 14 }}>
                            <p style={{ margin: '0 0 8px', fontFamily: FONT_BODY, fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: textMuted }}>Message</p>
                            <textarea
                                value={message}
                                onChange={e => setMessage(e.target.value)}
                                placeholder="Tell us what's on your mind…"
                                rows={4}
                                required
                                style={{
                                    width: '100%', padding: '10px 12px', borderRadius: 10,
                                    border: `1px solid ${inputBorder}`, background: inputBg,
                                    color: textPrimary, fontFamily: FONT_BODY, fontSize: '0.85rem',
                                    lineHeight: 1.5, resize: 'none', outline: 'none',
                                    boxSizing: 'border-box', transition: 'border-color 0.15s ease',
                                }}
                                onFocus={e => { e.currentTarget.style.borderColor = '#D4B254'; e.currentTarget.style.boxShadow = '0 0 0 2px rgba(212,178,84,0.18)' }}
                                onBlur={e => { e.currentTarget.style.borderColor = inputBorder; e.currentTarget.style.boxShadow = 'none' }}
                            />
                        </div>

                        {status === 'error' && (
                            <p style={{ margin: '0 0 10px', fontFamily: FONT_BODY, fontSize: '0.75rem', color: '#F87171' }}>
                                Something went wrong. Please try again.
                            </p>
                        )}

                        {/* Submit */}
                        <button
                            type="submit"
                            disabled={status === 'sending' || !message.trim()}
                            style={{
                                width: '100%', padding: '10px', borderRadius: 9999, border: 'none',
                                background: message.trim() && status !== 'sending'
                                    ? 'linear-gradient(135deg, #D4B254 0%, #B89741 100%)'
                                    : 'rgba(212,178,84,0.25)',
                                color: '#1A1A1A', fontFamily: FONT_BODY, fontWeight: 700,
                                fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em',
                                cursor: message.trim() && status !== 'sending' ? 'pointer' : 'not-allowed',
                                transition: 'all 0.18s ease', display: 'flex', alignItems: 'center',
                                justifyContent: 'center', gap: 8,
                            }}
                        >
                            {status === 'sending' ? (
                                <>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ animation: 'spin 1s linear infinite' }}>
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                    </svg>
                                    Sending…
                                </>
                            ) : (
                                <>
                                    <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                                    </svg>
                                    Send Feedback
                                </>
                            )}
                        </button>
                    </form>
                )}
            </div>

            {/* eslint-disable-next-line react/no-danger */}
            <style dangerouslySetInnerHTML={{ __html: '@keyframes spin { from { transform: rotate(0deg) } to { transform: rotate(360deg) } }' }} />
        </>
    );
};

export default FeedbackWidget;
